import { z } from 'zod';
import type { CampaignStatus, EmailStatus } from '../constants';
import { type IsoDateTime, RequiredText } from './common';
import { LeadSelection } from './leads';

/** A bulk send may queue at most this many emails; larger selections must be split by filter. */
export const MAX_CAMPAIGN_RECIPIENTS = 5000;

/**
 * Why a selected lead does not receive the bulk email. The first twelve mirror the outreach eligibility
 * policy (apps/api/src/domain/eligibility.ts); `duplicate_email` = the address already receives this email
 * via another lead in the batch; `not_owner` = the lead is assigned to another rep (reps may only email
 * leads they are allowed to edit).
 */
export const BULK_EXCLUSION_CODES = [
  'no_email',
  'invalid_email',
  'contact_invalid',
  'suppressed',
  'do_not_contact',
  'relationship_blocked',
  'business_closed',
  'consent_withdrawn',
  'no_consent',
  'consent_expired',
  'frequency_cap',
  'already_scheduled',
  'duplicate_email',
  'not_owner',
] as const;
export type BulkExclusionCode = (typeof BULK_EXCLUSION_CODES)[number];

export const BULK_EXCLUSION_LABELS: Record<BulkExclusionCode, string> = {
  no_email: 'No email address',
  invalid_email: 'Invalid email address',
  contact_invalid: 'Contact marked invalid',
  suppressed: 'Suppressed (unsubscribed, bounced or complained)',
  do_not_contact: 'Do not contact',
  relationship_blocked: 'Relationship blocks outreach',
  business_closed: 'Business permanently closed',
  consent_withdrawn: 'Consent withdrawn',
  no_consent: 'No consent recorded',
  consent_expired: 'Implied consent expired',
  frequency_cap: 'Emailed too recently (frequency cap)',
  already_scheduled: 'Another email already scheduled',
  duplicate_email: 'Duplicate email address',
  not_owner: 'Assigned to another salesperson',
};

const BulkContent = {
  selection: LeadSelection,
  templateId: z.uuid().nullish(),
  /** Send in the future (ISO date-time); omitted/null = as soon as the send window allows. */
  scheduledFor: z.iso.datetime({ offset: true }).nullish(),
};

/** POST /api/bulk-email/preview — subject/body default to the template's when omitted. */
export const BulkEmailPreviewBody = z.object({
  ...BulkContent,
  subject: z.string().max(250).optional(),
  body: z.string().max(20_000).optional(),
});
export type BulkEmailPreviewBody = z.infer<typeof BulkEmailPreviewBody>;

/** POST /api/bulk-email/send — the exact content reviewed + the review's confirmation token. */
export const BulkEmailSendBody = z.object({
  ...BulkContent,
  subject: z.string().trim().min(1, 'Subject is required').max(250),
  body: z.string().trim().min(1, 'Email body is required').max(20_000),
  name: RequiredText(200),
  confirmationToken: z.string().regex(/^[a-f0-9]{64}$/, 'Invalid confirmation token'),
  /** Human description of the selection shown in campaign history, e.g. "Hot leads in Montréal". */
  selectionDescription: z.string().trim().max(500).nullish(),
  /** Required when the review says `requiresTypedConfirmation`: must equal `SEND <recipient count>`. */
  typedConfirmation: z.string().max(40).optional(),
});
export type BulkEmailSendBody = z.infer<typeof BulkEmailSendBody>;

export type BulkRecipientDTO = {
  businessId: string;
  businessName: string;
  city: string | null;
  contactId: string | null;
  contactName: string | null;
  email: string;
};

export type BulkExclusionDTO = {
  businessId: string;
  businessName: string;
  city: string | null;
  contactId: string | null;
  contactName: string | null;
  email: string | null;
  code: BulkExclusionCode;
  /** Specific explanation, e.g. "Address was emailed 2 days ago (minimum 3 days between emails)". */
  reason: string;
};

/** One address shared by several selected leads: the first eligible lead keeps it, the rest are excluded. */
export type BulkDuplicateDTO = {
  email: string;
  kept: { businessId: string; businessName: string };
  excluded: { businessId: string; businessName: string }[];
};

export type BulkMissingVariableDTO = {
  variable: string;
  label: string;
  /** Recipients for whom the variable is empty. */
  count: number;
  /** Fallback text from the template (`{{ first_name | there }}`); null = renders blank. */
  fallback: string | null;
};

export type BulkSampleDTO = {
  businessId: string;
  businessName: string;
  contactName: string | null;
  toEmail: string;
  subject: string;
  /** Plain-text body including the compliance footer (what the recipient reads). */
  text: string;
  missing: string[];
};

/** Approximate delivery timeline from the dispatcher's per-minute/daily limits and the send window. */
export type BulkDeliveryEstimateDTO = {
  startsAt: IsoDateTime;
  finishesAt: IsoDateTime;
  /** Minutes of actual sending at the per-minute limit (excludes waiting for the window). */
  sendingMinutes: number;
  /** Number of sending days (window openings or 24h periods) the send spans. */
  days: number;
  perMinuteLimit: number;
  dailyLimit: number;
  window: { enabled: boolean; startHour: number; endHour: number; days: number[]; timezone: string };
};

export type BulkEmailPreviewResponse = {
  templateId: string | null;
  subject: string;
  body: string;
  scheduledFor: IsoDateTime | null;
  selectedLeads: number;
  recipientCount: number;
  excludedCount: number;
  /** First 500 recipients (see recipientCount for the total). */
  recipients: BulkRecipientDTO[];
  /** Up to 200 exclusions per reason (see countsByReason for totals). */
  excluded: BulkExclusionDTO[];
  countsByReason: Partial<Record<BulkExclusionCode, number>>;
  duplicates: BulkDuplicateDTO[];
  missingVariables: BulkMissingVariableDTO[];
  /** Blocking: malformed placeholders or unknown variables. */
  templateIssues: string[];
  /** Blocking: compliance settings incomplete or email provider not configured. */
  complianceIssues: string[];
  /**
   * Not blocking: set when these emails would go out through a sender that only records them (the log provider,
   * with no mailbox marked "Automated & bulk email") — they would show as sent but never arrive.
   */
  deliveryWarning: string | null;
  recipientLimit: number;
  requiresTypedConfirmation: boolean;
  /** Text to type when `requiresTypedConfirmation` (e.g. "SEND 37"). */
  confirmationText: string | null;
  confirmationToken: string;
  samples: BulkSampleDTO[];
  estimate: BulkDeliveryEstimateDTO | null;
};

export type BulkEmailSendResponse = {
  campaignId: string;
  /** false when this exact reviewed send was already submitted (replay) — the existing campaign is returned. */
  created: boolean;
  queued: number;
  excluded: number;
};

/** Email counts per status for one campaign (`replied` counts sent emails that got a reply). */
export type CampaignStatusCounts = {
  pending_approval: number;
  scheduled: number;
  sending: number;
  sent: number;
  delivered: number;
  bounced: number;
  failed: number;
  cancelled: number;
  uncertain: number;
  replied: number;
};

export type CampaignSummaryDTO = {
  id: string;
  name: string;
  subject: string;
  /** Derived: cancelled | completed (nothing left to send) | sending (some sent) | scheduled. */
  status: CampaignStatus;
  templateId: string | null;
  templateName: string | null;
  selectionDescription: string | null;
  totalSelected: number;
  totalQueued: number;
  totalExcluded: number;
  createdBy: string | null;
  createdAt: IsoDateTime;
  scheduledFor: IsoDateTime | null;
  cancelledAt: IsoDateTime | null;
  cancelledBy: string | null;
  counts: CampaignStatusCounts;
};

export type CampaignDetailDTO = CampaignSummaryDTO & {
  exclusionCounts: Partial<Record<BulkExclusionCode, number>>;
  /** Recipients still cancellable (pending approval or scheduled). */
  remaining: number;
};

export type CampaignRecipientDTO = {
  emailId: string;
  businessId: string | null;
  businessName: string | null;
  contactId: string | null;
  contactName: string | null;
  toEmail: string;
  status: EmailStatus;
  scheduledFor: IsoDateTime | null;
  sentAt: IsoDateTime | null;
  deliveredAt: IsoDateTime | null;
  bouncedAt: IsoDateTime | null;
  repliedAt: IsoDateTime | null;
  /** Failure / bounce / cancellation reason when relevant. */
  detail: string | null;
};

export type CampaignExclusionDTO = {
  id: string;
  businessId: string | null;
  businessName: string | null;
  contactId: string | null;
  contactName: string | null;
  email: string | null;
  code: BulkExclusionCode;
};

export const CampaignListQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
export type CampaignListQuery = z.infer<typeof CampaignListQuery>;

export const CAMPAIGN_RECIPIENT_FILTERS = ['all', 'queued', 'sent', 'replied', 'problem', 'cancelled'] as const;
export type CampaignRecipientFilter = (typeof CAMPAIGN_RECIPIENT_FILTERS)[number];

export const CampaignRecipientsQuery = z.object({
  filter: z.enum(CAMPAIGN_RECIPIENT_FILTERS).default('all'),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export type CampaignRecipientsQuery = z.infer<typeof CampaignRecipientsQuery>;

export const CampaignExclusionsQuery = z.object({
  code: z.enum(BULK_EXCLUSION_CODES).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export type CampaignExclusionsQuery = z.infer<typeof CampaignExclusionsQuery>;
